import React from 'react';
import { Tag } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { ReviewPoolItem, ReviewSourceTag, ReviewDiagnosisType } from '../types/reviewPool';
import { formatHubTimeShort, useHubZone } from '../utils/hubTime';

interface Props {
  item: ReviewPoolItem;
  selected?: boolean;
  onSelect?: (item: ReviewPoolItem) => void;
}

// 电话统一带 + 前缀（后端 countryCode 为数字 1 → 展示 +1 3341678802）
export function formatPhone(c: { phoneCountryCode?: string | number; phone?: string }): string {
  if (!c.phone) return '';
  const cc =
    c.phoneCountryCode != null && `${c.phoneCountryCode}`.trim() !== ''
      ? `+${`${c.phoneCountryCode}`.replace(/^\+/, '')} `
      : '';
  return `${cc}${c.phone}`;
}

// 来源标签配色（对齐原型：系统进单蓝 / 历史存量琥珀 / BD 青）
const SRC_STYLE: Record<ReviewSourceTag, React.CSSProperties> = {
  [ReviewSourceTag.System]: { color: '#3730a3', background: '#eef0ff', border: '1px solid #dfe2ff' },
  [ReviewSourceTag.Bd]: { color: '#0f766e', background: '#e6f7f3', border: '1px solid #cdeee6' },
  [ReviewSourceTag.History]: { color: '#b45309', background: '#fdf3e7', border: '1px solid #f6e0cf' },
};

const TAG_BASE: React.CSSProperties = {
  fontSize: 11,
  fontWeight: 700,
  padding: '2px 9px',
  borderRadius: 20,
  whiteSpace: 'nowrap',
  flex: '0 0 auto',
};

const ReviewAddressCard: React.FC<Props> = ({ item, selected, onSelect }) => {
  // hub 时区从 WarehouseContext 直接取，不逐层透传 props（列表→卡片链路上还有筛选/分组等中间层）
  const hubZone = useHubZone();
  const { t } = useTranslation();

  const sourceLabel: Record<ReviewSourceTag, string> = {
    [ReviewSourceTag.System]: t('pickup.addressReviewPool.sourceTag.system'),
    [ReviewSourceTag.Bd]: t('pickup.addressReviewPool.sourceTag.bd'),
    [ReviewSourceTag.History]: t('pickup.addressReviewPool.sourceTag.history'),
  };

  // 诊断标签：新地址 / 标准化失败（相似单独用 ⚠相似N 呈现，对齐原型）
  const diagTag = (() => {
    if (item.diagnosisType === ReviewDiagnosisType.New) {
      return { text: t('pickup.addressReviewPool.diagnosis.new'), style: { color: '#0f766e', background: '#e6f7f3' } };
    }
    if (item.diagnosisType === ReviewDiagnosisType.StandardizeFailed) {
      return {
        text: t('pickup.addressReviewPool.diagnosis.standardizeFailed'),
        style: { color: '#b45309', background: '#fdf3e7' },
      };
    }
    return null;
  })();
  const isSimilar = item.diagnosisType === ReviewDiagnosisType.Similar;

  /**
   * 当日单量标签（2.1 需求点6）。
   *
   * 只对系统进单 / BD 录入展示：历史存量条目是 golive 从 cooperation_warehouse 直插的、
   * 不关联任何订单，恒为 0 —— 显示「今日单量 0」会让运营误以为这个站点没生意。
   */
  const showTodayOrders = item.sourceTag !== ReviewSourceTag.History;

  // 展示联系人：优先第一个未拉黑的（PRD：卡片只展示一个联系人，「查看更多」在详情面板）
  const primary = item.contactPersons.find((c) => !c.inBlacklist) || item.contactPersons[0];

  const cardStyle: React.CSSProperties = {
    borderRadius: 12,
    padding: '13px 14px',
    cursor: 'pointer',
    transition: 'all .15s',
    ...(selected
      ? { background: '#fff', border: '2px solid #6d28d9', boxShadow: '0 6px 18px rgba(109,40,217,.12)' }
      : { background: '#fbf5f5', border: '1px solid #f2e6e6' }),
  };

  return (
    <div onClick={() => onSelect?.(item)} style={cardStyle}>
      {/* 地址 + 来源标签 */}
      <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 8, marginBottom: 8 }}>
        <div style={{ fontSize: 13, fontWeight: 700, lineHeight: 1.4, color: '#20242e' }}>{item.displayAddress}</div>
        <span style={{ ...TAG_BASE, ...SRC_STYLE[item.sourceTag] }}>{sourceLabel[item.sourceTag]}</span>
      </div>

      {/* 当日单量（2.1 需求点6）：帮运营判断这个地址值不值得建站 */}
      {showTodayOrders && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#3730a3',
            background: '#eef0ff',
            display: 'inline-block',
            padding: '1px 8px',
            borderRadius: 6,
            marginBottom: 8,
            marginRight: 6,
          }}
        >
          {t('pickup.addressReviewPool.todayOrders.tag', { count: item.todayOrderCount ?? 0 })}
        </div>
      )}

      {/* 站点 ID（历史存量已绑定既有站点时展示，对齐原型） */}
      {item.relatedWarehouseId != null && (
        <div
          style={{
            fontSize: 11,
            fontWeight: 700,
            color: '#6d28d9',
            background: '#f4f0ff',
            display: 'inline-block',
            padding: '1px 8px',
            borderRadius: 6,
            marginBottom: 8,
          }}
        >
          {t('pickup.addressReviewPool.list.stationId')}：{item.relatedWarehouseId}
        </div>
      )}

      {/* 联系人 + 电话 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, fontSize: 12.5, color: '#4a4658', marginBottom: 6 }}>
        <span>
          <span style={{ color: '#a7a2b6' }}>👤</span> <span style={{ fontWeight: 600 }}>{primary?.name || '-'}</span>
        </span>
        <span>
          <span style={{ color: '#a7a2b6' }}>📞</span> {primary ? formatPhone(primary) || '-' : '-'}
        </span>
        {primary?.inBlacklist && (
          <Tag color="red" icon={<StopOutlined />} className="!m-0">
            {t('pickup.addressReviewPool.list.blacklisted')}
          </Tag>
        )}
      </div>

      {/* 渠道 + 诊断标签 / 相似 */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12, color: '#8a8698' }}>
        <span style={{ display: 'flex', minWidth: 0, alignItems: 'center', gap: 4 }}>
          <span style={{ color: '#a7a2b6' }}>🛍</span>
          <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {item.channelNames.join('、') || '-'}
          </span>
        </span>
        {diagTag && <span style={{ ...TAG_BASE, marginLeft: 'auto', ...diagTag.style }}>{diagTag.text}</span>}
        {isSimilar && (
          <span style={{ ...TAG_BASE, marginLeft: 'auto', color: '#c2410c', background: '#fff1e8' }}>
            ⚠ {t('pickup.addressReviewPool.diagnosis.similar')} {item.similarCount ?? ''}
          </span>
        )}
      </div>

      {/* 分隔线 + 创建时间 */}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          gap: 6,
          borderTop: '1px solid #f2eef6',
          paddingTop: 8,
          marginTop: 8,
          fontSize: 11.5,
          color: '#a7a2b6',
        }}
      >
        <span>
          🕐 {t('pickup.addressReviewPool.list.createdTime')}{' '}
          {formatHubTimeShort(item.firstOrderTime, hubZone)}
        </span>
      </div>
    </div>
  );
};

export default ReviewAddressCard;
